import { useEffect, useState } from 'react';
import { X, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { crmApi, type CrmProduct } from '../../services/api';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * Add or edit a catalogue SKU.
 *
 * Two authority tiers, mirroring the server exactly (services/crm_catalog_service):
 *
 *   catalog:manage — description, unit, billing period, family, availability
 *   catalog:admin  — the NAME and the PRICES
 *
 * A name or a price is what a customer reads off a quote, so changing either
 * re-characterises what was sold. The elevated fields are DISABLED rather than
 * hidden for a manager: hiding them makes the product look like it has no
 * price, whereas a locked field with a reason says who to ask.
 *
 * The SKU itself is immutable once created — every quote line that ever used it
 * references the SKU, not the document id.
 */

interface Props {
  product: CrmProduct | null;      // null = creating
  onClose: () => void;
  onSaved: () => void;
}

const BILLING = ['annual', 'monthly', 'one-time', 'per-hour', 'per-task'];

/** Minor units <-> the major-unit string a person actually types. */
const toMajor = (minor?: number) => ((minor || 0) / 100).toString();
const toMinor = (major: string) => Math.round(parseFloat(major || '0') * 100);

export default function ProductEditor({ product, onClose, onSaved }: Props) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canPrice = hasPermission('catalog:admin');
  const creating = product === null;

  const [form, setForm] = useState({
    sku: '', catalogId: 'research', familyId: '', familyName: '',
    familyType: 'platform_license', name: '', description: '',
    basePrice: '0', cost: '0', msrp: '',
    billingPeriod: 'annual', unit: '', volumetric: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!product) return;
    setForm({
      sku: product.sku, catalogId: product.catalogId,
      familyId: product.familyId, familyName: product.familyName || '',
      familyType: product.familyType || 'platform_license',
      name: product.name, description: product.description || '',
      basePrice: toMajor(product.basePriceMinor),
      cost: toMajor(product.costMinor),
      msrp: toMajor(product.msrpMinor),
      billingPeriod: product.billingPeriod || 'annual',
      unit: product.unit || '', volumetric: Boolean(product.volumetric),
    });
  }, [product]);

  const set = (k: string, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (creating) {
        await crmApi.createProduct({
          sku: form.sku.trim(),
          catalogId: form.catalogId, familyId: form.familyId,
          familyName: form.familyName, familyType: form.familyType,
          name: form.name, description: form.description,
          basePriceMinor: toMinor(form.basePrice),
          costMinor: toMinor(form.cost),
          msrpMinor: form.msrp ? toMinor(form.msrp) : toMinor(form.basePrice),
          billingPeriod: form.billingPeriod, unit: form.unit,
          volumetric: form.volumetric,
        });
        toast.success(`${form.sku.trim()} added`);
      } else {
        // Only send what this principal may change. Sending a price the server
        // will refuse turns an allowed description edit into a 403.
        const payload: Partial<CrmProduct> = {
          description: form.description, billingPeriod: form.billingPeriod,
          unit: form.unit, volumetric: form.volumetric,
          familyId: form.familyId, familyName: form.familyName,
          familyType: form.familyType,
        };
        if (canPrice) {
          payload.name = form.name;
          payload.basePriceMinor = toMinor(form.basePrice);
          payload.costMinor = toMinor(form.cost);
          payload.msrpMinor = toMinor(form.msrp || form.basePrice);
        }
        await crmApi.updateProduct(product!._id, payload);
        toast.success(`${product!.sku} saved`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the product');
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-60 disabled:cursor-not-allowed';
  const label = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-gray-800 shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {creating ? 'New SKU' : `Edit ${product!.sku}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        {!canPrice && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            <Lock size={14} className="mt-0.5 shrink-0" />
            <span>
              The name and prices need <strong>catalog:admin</strong>, held by the
              CRM admin role. They are what a customer reads off a quote, so
              changing them re-characterises what was sold.
            </span>
          </div>
        )}

        <div className="p-5 grid grid-cols-2 gap-4">
          <div>
            <label className={label}>SKU</label>
            <input
              value={form.sku}
              disabled={!creating}
              onChange={(e) => set('sku', e.target.value)}
              placeholder="RES-FWK-ASAF"
              className={`${field} font-mono`}
            />
            <p className="mt-1 text-[11px] text-gray-400">
              {creating
                ? 'UPPERCASE segments separated by hyphens. Not case-corrected — it is printed on quotes.'
                : 'Immutable: every quote line that used it references this SKU.'}
            </p>
          </div>

          <div>
            <label className={label}>
              Name {!canPrice && <Lock size={10} className="inline ml-1 -mt-0.5" />}
            </label>
            <input value={form.name} disabled={!canPrice}
                   onChange={(e) => set('name', e.target.value)} className={field} />
          </div>

          <div className="col-span-2">
            <label className={label}>Description</label>
            <textarea value={form.description} rows={3}
                      onChange={(e) => set('description', e.target.value)}
                      className={field} />
          </div>

          <div>
            <label className={label}>Catalog</label>
            <select value={form.catalogId} disabled={!creating}
                    onChange={(e) => set('catalogId', e.target.value)} className={field}>
              <option value="research">research</option>
              <option value="pmo">pmo</option>
            </select>
          </div>

          <div>
            <label className={label}>Family id</label>
            <input value={form.familyId} onChange={(e) => set('familyId', e.target.value)}
                   placeholder="res-frameworks" className={field} />
          </div>

          <div className="col-span-2">
            <label className={label}>Family name</label>
            <input value={form.familyName}
                   onChange={(e) => set('familyName', e.target.value)}
                   placeholder="Frameworks &amp; Assessments" className={field} />
          </div>

          <div>
            <label className={label}>
              List price {!canPrice && <Lock size={10} className="inline ml-1 -mt-0.5" />}
            </label>
            <input value={form.basePrice} disabled={!canPrice} inputMode="decimal"
                   onChange={(e) => set('basePrice', e.target.value)} className={field} />
          </div>

          <div>
            <label className={label}>
              Cost {!canPrice && <Lock size={10} className="inline ml-1 -mt-0.5" />}
            </label>
            <input value={form.cost} disabled={!canPrice} inputMode="decimal"
                   onChange={(e) => set('cost', e.target.value)} className={field} />
          </div>

          <div>
            <label className={label}>
              MSRP {!canPrice && <Lock size={10} className="inline ml-1 -mt-0.5" />}
            </label>
            <input value={form.msrp} disabled={!canPrice} inputMode="decimal"
                   placeholder="defaults to the list price"
                   onChange={(e) => set('msrp', e.target.value)} className={field} />
          </div>

          <div>
            <label className={label}>Billing period</label>
            <select value={form.billingPeriod}
                    onChange={(e) => set('billingPeriod', e.target.value)}
                    className={field}>
              {BILLING.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div>
            <label className={label}>Unit</label>
            <input value={form.unit} onChange={(e) => set('unit', e.target.value)}
                   placeholder="framework assessment" className={field} />
          </div>

          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={form.volumetric}
                     onChange={(e) => set('volumetric', e.target.checked)} />
              Volumetric
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose}
                  className="px-3 py-1.5 text-sm rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !form.sku.trim() || !form.name.trim()}
                  className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : creating ? 'Add SKU' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
